import { Platform } from 'react-native'
import {
  ACCOUNT_ADMIN_NAV,
  ACCOUNT_ADMIN_NAV_ITEMS,
  ACCOUNT_ADMIN_PUBLIC_ROUTE,
  ACCOUNT_ADMIN_WEB_ONLY_NAV_IDS,
  type AccountAdminNavId,
} from '../constants/accountAdmin.constants'
import type { AccountAdminCopy } from '../../i18n/translations/accountAdminCopy/accountAdminCopy.en'
import { accountAdminCopyEn } from '../../i18n/translations/accountAdminCopy/accountAdminCopy.en'
import {
  ACCOUNT_ADMIN_DRAWER_MAX_WIDTH,
  ACCOUNT_ADMIN_DRAWER_WIDTH_FRACTION,
  ACCOUNT_ADMIN_WEB_DRAWER_PUSH_MIN_REMAINING_WIDTH_PX,
} from '../constants/accountAdminNav.constants'

const ACCOUNT_ADMIN_NAV_ID_SET = new Set<string>(Object.values(ACCOUNT_ADMIN_NAV))

export function parseAccountAdminNavParam(raw: unknown): AccountAdminNavId | null {
  const s =
    typeof raw === 'string'
      ? raw.trim()
      : Array.isArray(raw) && typeof raw[0] === 'string'
        ? raw[0].trim()
        : ''
  if (!s || !ACCOUNT_ADMIN_NAV_ID_SET.has(s)) {
    return null
  }
  return s as AccountAdminNavId
}

/** Stable key for `?nav=` (raw query value, or sentinel when absent). Used to avoid re-applying URL on every user refresh. */
export function getAccountAdminNavQueryKey(raw: unknown): string {
  const s =
    typeof raw === 'string'
      ? raw.trim()
      : Array.isArray(raw) && typeof raw[0] === 'string'
        ? raw[0].trim()
        : ''
  return s.length > 0 ? s : '__none__'
}

/** Deep link to `/account-admin` with a drawer tab (e.g. Start here). */
export function buildAccountAdminNavPath(navId: AccountAdminNavId): string {
  return `${ACCOUNT_ADMIN_PUBLIC_ROUTE}?nav=${encodeURIComponent(navId)}`
}

/** Drawer panel width in px (same formula as `AccountAdminNavDrawer`). */
export function getAccountAdminDrawerWidthPx(windowWidth: number): number {
  return Math.min(windowWidth * ACCOUNT_ADMIN_DRAWER_WIDTH_FRACTION, ACCOUNT_ADMIN_DRAWER_MAX_WIDTH)
}

/**
 * Horizontal inset (px) to apply to web shells when the org drawer is open.
 * Returns 0 when the viewport is too narrow to keep a readable main column beside the drawer.
 */
export function getEffectiveAccountAdminWebDrawerShiftPx(
  open: boolean,
  windowWidth: number,
): number {
  if (!open || windowWidth <= 0) {
    return 0
  }
  const drawerW = getAccountAdminDrawerWidthPx(windowWidth)
  const remaining = windowWidth - drawerW
  if (remaining < ACCOUNT_ADMIN_WEB_DRAWER_PUSH_MIN_REMAINING_WIDTH_PX) {
    return 0
  }
  return drawerW
}

/**
 * True when the drawer covers the page (no side-by-side “push” column). Used to auto-close the
 * menu after a nav selection on mobile web and all native surfaces.
 */
export function isAccountAdminDrawerOverlayMode(windowWidth: number): boolean {
  if (windowWidth <= 0) {
    return true
  }
  const drawerW = getAccountAdminDrawerWidthPx(windowWidth)
  const remaining = windowWidth - drawerW
  return remaining < ACCOUNT_ADMIN_WEB_DRAWER_PUSH_MIN_REMAINING_WIDTH_PX
}

/**
 * True when the user is on the org-admin surface (any sub-tab: Start here, Branding, Members, …).
 * Used so the drawer expands the parent group that contains those items.
 */
export function isAccountAdminOrgNavSurfacePath(pathname: string): boolean {
  const p = pathname === '' ? '/' : pathname
  return p === '/account-admin' || p.startsWith('/account-admin/')
}

export function getAccountAdminNavLabel(
  id: AccountAdminNavId,
  copy: AccountAdminCopy = accountAdminCopyEn,
): string {
  switch (id) {
    case ACCOUNT_ADMIN_NAV.START_HERE:
      return copy.START_HERE
    case ACCOUNT_ADMIN_NAV.BRANDING:
      return copy.BRANDING
    case ACCOUNT_ADMIN_NAV.MEMBERS:
      return copy.MEMBERS
    case ACCOUNT_ADMIN_NAV.SETTINGS:
      return copy.SETTINGS
    case ACCOUNT_ADMIN_NAV.PLAN_BILLING:
      return copy.PLAN_BILLING
    case ACCOUNT_ADMIN_NAV.INVOICE_DETAILS:
      return copy.INVOICE_DETAILS
  }
}

/** Nav items visible on the current platform (billing tabs are web-only). */
export function getAccountAdminNavItemsForPlatform(): AccountAdminNavId[] {
  if (Platform.OS === 'web') {
    return ACCOUNT_ADMIN_NAV_ITEMS
  }
  return ACCOUNT_ADMIN_NAV_ITEMS.filter((id) => !ACCOUNT_ADMIN_WEB_ONLY_NAV_IDS.has(id))
}

/** Condensed labels for the mobile horizontal tab strip (narrow screens). */
export function getAccountAdminNavShortLabel(
  id: AccountAdminNavId,
  copy: AccountAdminCopy = accountAdminCopyEn,
): string {
  switch (id) {
    case ACCOUNT_ADMIN_NAV.START_HERE:
      return copy.navShort.START_HERE
    case ACCOUNT_ADMIN_NAV.BRANDING:
      return copy.navShort.BRANDING
    case ACCOUNT_ADMIN_NAV.MEMBERS:
      return copy.navShort.MEMBERS
    case ACCOUNT_ADMIN_NAV.SETTINGS:
      return copy.navShort.SETTINGS
    case ACCOUNT_ADMIN_NAV.PLAN_BILLING:
      return copy.navShort.PLAN_BILLING
    case ACCOUNT_ADMIN_NAV.INVOICE_DETAILS:
      return copy.navShort.INVOICE_DETAILS
  }
}
